import React, { useState, useCallback } from 'react';
import { DropdownMenuItem } from '../../ui/dropdown-menu';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { useLazyCommand, useDomainEvent, translate, useEvent } from '@soap/modules';
import { UserPlus, X } from 'lucide-react';
import { UnarchiveStudentDialog } from './UnarchiveStudentDialog';

interface AssignSubAccountDialogProps {
  studentId: string;
  studentName: string;
  studentEmail: string;
}

export function AssignSubAccountDialog({ studentId, studentName, studentEmail }: AssignSubAccountDialogProps) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showUnarchiveStudentDialog, setShowUnarchiveStudentDialog] = useState(false);
  const [unarchiveStudentId, setUnarchiveStudentId] = useState<string | null>(null);
  const [unarchiveStudentName, setUnarchiveStudentName] = useState<string>('');

  const [sendCommand] = useLazyCommand('C254v1_AssignSubAccountToStudent');
  const triggerCloseContextMenu = useDomainEvent({
    eventName: "close-context-menu",
  });

  const close = useCallback(() => {
    setOpen(false);
    triggerCloseContextMenu();
  }, [triggerCloseContextMenu]);

  // Initialize email when dialog opens
  React.useEffect(() => {
    if (open) {
      setEmail(studentEmail || '');
    }
  }, [open, studentEmail]);

  // Listen for successful assignment event
  useEvent({
    eventName: "E202v1_StudentInviteSent",
    onEventReceived: () => {
      close();
    }
  });

  // Listen for email reuse requires unarchive event
  useEvent({
    eventName: "E203v1_EmailReuseRequiresUnarchive",
    onEventReceived: (event: any) => {
      const errorCode = event.e203_ErrorCode;
      if (errorCode === "EmailReuseRequiresUnarchiveStudent" && isSubmitting) {
        setUnarchiveStudentId(event.e203_StudentId);
        // Extract student name from message if available, or use a default
        const message = event.e203_Message || '';
        const nameMatch = message.match(/\(([^)]+)\)/);
        setUnarchiveStudentName(nameMatch ? nameMatch[1] : 'this student');
        setShowUnarchiveStudentDialog(true);
        setIsSubmitting(false);
      }
    }
  });

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (!email.trim()) return;

    setIsSubmitting(true);
    try {
      await sendCommand({
        C254_StudentId: studentId,
        C254_Email: email.trim()
      });
    } catch (error) {
      console.error('Failed to assign sub-account:', error);
      setIsSubmitting(false);
    }
  };

  if (!open) {
    return (
      <DropdownMenuItem
        className="w-full justify-start"
        data-dialog-item
        onClick={() => setOpen(true)}
      >
        <UserPlus className="w-4 h-4 mr-2" />
        Give Login
      </DropdownMenuItem>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-5">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-2xl font-bold text-gray-900">Give Login to Student</h2>
            <button
              onClick={close}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="studentName" className="text-sm font-semibold text-gray-900">Student Name</Label>
              <Input
                id="studentName"
                value={studentName}
                disabled
                className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-semibold text-gray-900">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-action-blue focus:border-action-blue"
              />
              <p className="text-sm text-gray-500">
                An invitation will be sent to this email address to create a user account for this student.
              </p>
            </div>

            <div className="flex justify-end space-x-3 pt-3">
              <button
                type="button"
                onClick={close}
                disabled={isSubmitting}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!email.trim() || isSubmitting}
                className="px-6 py-2 bg-action-blue hover:bg-action-blue-dark text-white rounded-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed min-w-[140px]"
              >
                {isSubmitting ? "Sending..." : "Send Invitation"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {showUnarchiveStudentDialog && unarchiveStudentId && (
        <UnarchiveStudentDialog
          studentId={unarchiveStudentId}
          studentName={unarchiveStudentName}
          onClose={() => {
            setShowUnarchiveStudentDialog(false);
            setUnarchiveStudentId(null);
            setUnarchiveStudentName('');
            close(); // Close the assign sub-account dialog too
          }}
        />
      )}
    </div>
  );
}
